/*
  Floating terminal window — site-wide.

  Manages three behaviours:
    1. Consent flow — shows a cookie consent pane on first visit; on click
       persists consent in localStorage + (best-effort) calls Shopify's
       Customer Privacy API, then switches to the interactive pane.
    2. Drag — header bar is the drag handle. Window is constrained to the
       viewport with an 8px margin.
    3. Minimize / restore — header-only state via a class toggle.

  The interactive terminal logic itself is owned by ms-poster-terminal.js
  which auto-inits every [data-ms-terminal] on the page (including this
  window's hidden interactive pane). When we unhide the pane after consent,
  the existing event listeners are live and the terminal works.
*/
(() => {
  const KEY_CONSENT  = 'ms_cookie_consent';
  const KEY_MIN      = 'ms_terminal_minimized';

  const KEY_POSITION = 'ms_terminal_position';
  const KEY_SIZE     = 'ms_terminal_size';
  const MIN_W        = 520;  // matches the default window width  (4:3)
  const MIN_H        = 390;  // matches the default window height (4:3)

  const wnd = document.querySelector('[data-ms-terminal-window]');
  if (!wnd) return;

  // Treat <=600px as mobile — MUST match the CSS media query that hides the
  // minimize button. On mobile the terminal exists only to serve the cookie
  // consent banner, which must not be collapsible (cookie-bar compliance),
  // so minimize is disabled entirely below this width.
  const isMobile = () => window.matchMedia('(max-width: 600px)').matches;

  // ---- has the visitor responded to the cookie banner yet? ----
  // "Responded" = made a choice (Accept all OR Essential only) — that is
  // what writes KEY_CONSENT. Minimize is gated on this so the consent banner
  // can never be collapsed before a choice is made (cookie-bar compliance).
  // Read early because willStartMinimized depends on it.
  let storedConsent = null;
  try { storedConsent = JSON.parse(localStorage.getItem(KEY_CONSENT) || 'null'); } catch (e) {}
  const hasResponded = !!(storedConsent && storedConsent.level);

  // ---- restore saved window size + position ----
  // Skip size restore if the window is going to be minimized on load —
  // inline width/height would override the .ms-terminal-window--minimized
  // CSS rule (inline beats class). The expand handler re-applies size.
  // Never start minimized on mobile (terminal is consent-only there) or
  // before the visitor has responded to the banner — either would collapse
  // the consent banner before a choice is made.
  const willStartMinimized = localStorage.getItem(KEY_MIN) === '1' && !isMobile() && hasResponded;
  if (!willStartMinimized) {
    try {
      const size = JSON.parse(localStorage.getItem(KEY_SIZE) || 'null');
      if (size && typeof size.width === 'number' && typeof size.height === 'number') {
        const w = Math.max(MIN_W, Math.min(window.innerWidth  - 16, size.width));
        const h = Math.max(MIN_H, Math.min(window.innerHeight - 16, size.height));
        wnd.style.width  = w + 'px';
        wnd.style.height = h + 'px';
      }
    } catch (e) { /* ignore */ }
  }
  try {
    const pos = JSON.parse(localStorage.getItem(KEY_POSITION) || 'null');
    if (pos && typeof pos.left === 'number' && typeof pos.top === 'number') {
      requestAnimationFrame(() => {
        const rect = wnd.getBoundingClientRect();
        const nx = Math.max(8, Math.min(window.innerWidth  - rect.width  - 8, pos.left));
        const ny = Math.max(8, Math.min(window.innerHeight - rect.height - 8, pos.top));
        wnd.style.left   = nx + 'px';
        wnd.style.top    = ny + 'px';
        wnd.style.bottom = 'auto';
        wnd.style.right  = 'auto';
      });
    }
  } catch (e) { /* ignore */ }

  const consentPane     = wnd.querySelector('[data-ms-consent-pane]');
  const interactivePane = wnd.querySelector('[data-ms-terminal]');
  const dragHandle      = wnd.querySelector('[data-ms-terminal-drag]');
  const minBtn          = wnd.querySelector('[data-ms-terminal-min]');

  // ---- consent / interactive transition ----
  const showInteractive = () => {
    if (consentPane) consentPane.hidden = true;
    if (interactivePane) {
      interactivePane.hidden = false;
      // Focus the input so the user can start typing right away. Skip if
      // the window is hidden (e.g. mobile post-consent — see CSS rule
      // .ms-terminal-window--consented under the mobile media query).
      const input = interactivePane.querySelector('[data-input]');
      if (input) {
        setTimeout(() => {
          if (wnd.offsetParent !== null) {
            try { input.focus(); } catch (e) {}
          }
        }, 50);
      }
    }
    // Flag that consent has been granted. On mobile a CSS rule then
    // hides the window — the interactive command-line UX is poor on
    // touch keyboards, so we keep the terminal as a desktop-only feature
    // once the user has cleared the consent flow.
    wnd.classList.add('ms-terminal-window--consented');
  };

  if (hasResponded) {
    showInteractive();
  } else {
    const setConsent = (level) => {
      try { localStorage.setItem(KEY_CONSENT, JSON.stringify({ level: level, at: Date.now() })); } catch (e) {}
      if (window.Shopify && window.Shopify.customerPrivacy) {
        try { window.Shopify.customerPrivacy.setTrackingConsent(level === 'all', () => {}); } catch (e) {}
      }
      showInteractive();
    };

    wnd.querySelector('[data-ms-cookie-accept]')?.addEventListener('click', () => setConsent('all'));
    wnd.querySelector('[data-ms-cookie-decline]')?.addEventListener('click', () => setConsent('essential'));
  }

  // ---- minimize / restore ----
  // Restore minimized state from localStorage so the user's choice
  // persists across page loads.
  const syncMinAria = () => {
    if (!minBtn) return;
    const isMin = wnd.classList.contains('ms-terminal-window--minimized');
    minBtn.setAttribute('aria-label', isMin ? 'Expand' : 'Minimize');
  };
  if (willStartMinimized) {
    wnd.classList.add('ms-terminal-window--minimized');
  }
  syncMinAria();

  // Apply saved size from localStorage (used when restoring from minimized).
  const applyStoredSize = () => {
    try {
      const size = JSON.parse(localStorage.getItem(KEY_SIZE) || 'null');
      if (size && typeof size.width === 'number' && typeof size.height === 'number') {
        const w = Math.max(MIN_W, Math.min(window.innerWidth  - 16, size.width));
        const h = Math.max(MIN_H, Math.min(window.innerHeight - 16, size.height));
        wnd.style.width  = w + 'px';
        wnd.style.height = h + 'px';
      }
    } catch (e) {}
  };

  minBtn?.addEventListener('click', () => {
    // Minimize is disabled on mobile (the button is display:none there via
    // CSS; this is a defensive no-op in case the click ever fires). The
    // cookie consent banner must not be collapsible on mobile.
    if (isMobile()) return;
    // Cookie-bar compliance: never allow collapsing until the visitor has
    // responded to the consent banner. The button is display:none until the
    // --consented class is set (CSS); this is the defensive runtime guard.
    if (!wnd.classList.contains('ms-terminal-window--consented')) return;
    const wasMin = wnd.classList.contains('ms-terminal-window--minimized');
    if (wasMin) {
      // Expanding — re-apply the user's preferred size.
      wnd.classList.remove('ms-terminal-window--minimized');
      applyStoredSize();
    } else {
      // Minimizing — clear inline size so the CSS minimized rule (240px
      // wide / auto height) actually wins.
      wnd.style.width = '';
      wnd.style.height = '';
      wnd.classList.add('ms-terminal-window--minimized');
    }
    const isMin = wnd.classList.contains('ms-terminal-window--minimized');
    try { localStorage.setItem(KEY_MIN, isMin ? '1' : '0'); } catch (e) {}
    syncMinAria();
  });

  // Only the smiley button restores the window — the header bar stays
  // drag-only so the user can move the minimized window around without
  // accidentally expanding it on mouseup.

  // ---- drag from header ----
  if (dragHandle) {
    let dragging = false;
    let startX = 0, startY = 0, originX = 0, originY = 0;
    let didMove = false;

    const point = (e) => (e.touches && e.touches[0]) ? e.touches[0] : e;

    const start = (e) => {
      if (e.type === 'mousedown' && e.button !== 0) return;
      if (e.target.closest('button')) return;
      const rect = wnd.getBoundingClientRect();
      // Pin to top-left coords for simpler drag math (default position is
      // bottom-right anchored, so switch anchoring on first drag).
      wnd.style.left   = rect.left + 'px';
      wnd.style.top    = rect.top  + 'px';
      wnd.style.bottom = 'auto';
      wnd.style.right  = 'auto';
      const p = point(e);
      startX = p.clientX;
      startY = p.clientY;
      originX = rect.left;
      originY = rect.top;
      dragging = true;
      didMove = false;
      dragHandle.classList.add('is-dragging');
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const move = (e) => {
      if (!dragging) return;
      const p = point(e);
      const dx = p.clientX - startX;
      const dy = p.clientY - startY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didMove = true;
      const rect = wnd.getBoundingClientRect();
      const nx = Math.max(8, Math.min(window.innerWidth  - rect.width  - 8, originX + dx));
      const ny = Math.max(8, Math.min(window.innerHeight - rect.height - 8, originY + dy));
      wnd.style.left = nx + 'px';
      wnd.style.top  = ny + 'px';
    };

    const end = () => {
      if (!dragging) return;
      dragging = false;
      dragHandle.classList.remove('is-dragging');
      document.body.style.userSelect = '';
      // Persist position so the window stays where the user put it across
      // page navigations (and across browser restarts — localStorage).
      if (didMove) {
        try {
          const rect = wnd.getBoundingClientRect();
          localStorage.setItem(KEY_POSITION, JSON.stringify({
            left: rect.left, top: rect.top,
          }));
        } catch (e) {}
      }
      // If the user only clicked (no movement), the click handler above
      // takes care of toggling minimize. didMove flag prevents the click
      // toggle from firing after a real drag.
    };

    dragHandle.addEventListener('mousedown', start);
    dragHandle.addEventListener('touchstart', start, { passive: false });
    document.addEventListener('mousemove', move);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('mouseup', end);
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', end);
  }

  // ---- corner resize (bottom-right) ----
  // Same mouse + touch pattern as drag, but updates width/height instead of
  // position. Clamped to MIN_W × MIN_H on the small end and viewport-bounded
  // on the large end. Size is persisted to localStorage.
  const resizeHandle = wnd.querySelector('[data-ms-terminal-resize]');
  if (resizeHandle) {
    let resizing = false;
    let startX = 0, startY = 0, startW = 0, startH = 0;

    const pointR = (e) => (e.touches && e.touches[0]) ? e.touches[0] : e;

    const rStart = (e) => {
      if (e.type === 'mousedown' && e.button !== 0) return;
      const rect = wnd.getBoundingClientRect();
      // Pin to top-left so subsequent width/height changes don't fight with
      // the default bottom-right anchored CSS.
      wnd.style.left   = rect.left + 'px';
      wnd.style.top    = rect.top  + 'px';
      wnd.style.bottom = 'auto';
      wnd.style.right  = 'auto';
      startW = rect.width;
      startH = rect.height;
      const p = pointR(e);
      startX = p.clientX;
      startY = p.clientY;
      resizing = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';
      e.preventDefault();
      e.stopPropagation();
    };

    const rMove = (e) => {
      if (!resizing) return;
      const p = pointR(e);
      const rect = wnd.getBoundingClientRect();
      const maxW = window.innerWidth  - rect.left - 8;
      const maxH = window.innerHeight - rect.top  - 8;
      const newW = Math.max(MIN_W, Math.min(maxW, startW + (p.clientX - startX)));
      const newH = Math.max(MIN_H, Math.min(maxH, startH + (p.clientY - startY)));
      wnd.style.width  = newW + 'px';
      wnd.style.height = newH + 'px';
    };

    const rEnd = () => {
      if (!resizing) return;
      resizing = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try {
        const rect = wnd.getBoundingClientRect();
        localStorage.setItem(KEY_SIZE, JSON.stringify({
          width: rect.width, height: rect.height,
        }));
      } catch (e) {}
    };

    resizeHandle.addEventListener('mousedown', rStart);
    resizeHandle.addEventListener('touchstart', rStart, { passive: false });
    document.addEventListener('mousemove', rMove);
    document.addEventListener('touchmove', rMove, { passive: false });
    document.addEventListener('mouseup', rEnd);
    document.addEventListener('touchend', rEnd);
    document.addEventListener('touchcancel', rEnd);
  }
})();
